"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  CategoryTable,
  GameteLabel,
  GeneLabels,
  SquareTable,
  tintFor,
} from "@/components/tools/biology-genetics/punnett-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CrossResult,
  type Dominance,
  type GeneInput,
  geneListText,
  isCasedLetter,
  reduceRatio,
  runCross,
  type SexSystem,
} from "@/lib/tool-kit/biology-genetics/punnett";

/** English strings. A translated content file overrides these through `ui`. */
export const PUNNETT_SQUARE_CALCULATOR_STRINGS = {
  genesLabel: "Number of genes in the cross",
  genesOne: "Monohybrid (1 gene)",
  genesTwo: "Dihybrid (2 genes)",
  genesThree: "Trihybrid (3 genes)",
  sexLinked: "Gene 1 is sex-linked",
  systemLabel: "Sex-determination system",
  systemXy: "XX female, XY male (mammals, fruit flies)",
  systemZw: "ZZ male, ZW female (birds, butterflies)",
  sexNote:
    "Parent 1 is the female and parent 2 the male. The sex-linked gene sits on the sex chromosomes; genes 2 and 3 are autosomal.",
  geneHeading: "Gene {n}",
  symbolLabel: "Letter for gene {n}",
  symbolHint: "One letter — the capital is the dominant allele.",
  dominanceLabel: "Dominance for gene {n}",
  dominanceComplete: "Complete dominance",
  dominanceIncomplete: "Incomplete dominance (heterozygote is intermediate)",
  dominanceCodominant: "Codominance (heterozygote shows both)",
  parent1: "Parent 1",
  parent2: "Parent 2",
  female: "Female parent",
  male: "Male parent",
  genotypeLabel: "{parent}: genotype for gene {n}",
  kindHomDominant: "homozygous dominant",
  kindHet: "heterozygous",
  kindHomRecessive: "homozygous recessive",
  kindHemiDominant: "hemizygous, dominant allele",
  kindHemiRecessive: "hemizygous, recessive allele",
  namesSummary: "Name the phenotypes (optional)",
  nameLabel: "Gene {n}: phenotype of {genotype}",
  phenDominant: "dominant ({allele})",
  phenRecessive: "recessive ({allele})",
  phenHomozygous: "{genotype} phenotype",
  phenIntermediate: "{genotype} (intermediate)",
  phenBoth: "{genotype} (both expressed)",
  phenotypeFemale: "Female",
  phenotypeMale: "Male",
  phenotypeWithSex: "{sex}: {traits}",
  results: "Results",
  gametesHeading: "Gametes",
  gametesLine: "{parent}:",
  squareHeading: "Punnett square",
  squareCaption:
    "Punnett square. {rowParent} gametes run down the side and {colParent} gametes across the top.",
  squareCorner: "Gametes",
  boxLabel: "Offspring {genotype}, {phenotype}",
  boxesSummary:
    "one: {count} box, each with probability {percent}%|other: {count} boxes, each with probability {percent}%",
  phenotypesHeading: "Phenotypes",
  genotypesHeading: "Genotypes",
  phenotypeColumn: "Phenotype",
  genotypeColumn: "Genotype",
  boxesColumn: "Boxes",
  fractionColumn: "Fraction",
  probabilityColumn: "Probability",
  ratioHeading: "Ratios",
  genotypicRatio: "Genotypic ratio",
  phenotypicRatio: "Phenotypic ratio",
  ratioLargest: "{label}, largest first",
  copyRatios: "Copy ratios",
  copied: "Copied!",
  errorSymbol:
    "Give each gene one letter that has a capital and a lower-case form, such as A and a.",
  errorDuplicate: "Each gene needs a different letter.",
} as const;

type Strings = typeof PUNNETT_SQUARE_CALCULATOR_STRINGS;
type GeneCount = "1" | "2" | "3";
type Kind = "autosomal" | "homogametic" | "hemizygous";

interface GeneState {
  symbol: string;
  dominance: Dominance;
  parent1: string;
  parent2: string;
  names: [string, string, string];
}

const DEFAULT_SYMBOLS = ["A", "B", "C"] as const;
const SEX_CHROM: Record<SexSystem, { homo: string; hetero: string }> = {
  xy: { homo: "X", hetero: "Y" },
  zw: { homo: "Z", hetero: "W" },
};

function initialGene(index: number): GeneState {
  return {
    symbol: DEFAULT_SYMBOLS[index],
    dominance: "complete",
    parent1: "1",
    parent2: "1",
    names: ["", "", ""],
  };
}

function genotypeText(
  symbol: string,
  copies: number,
  kind: Kind,
  system: SexSystem
): string {
  const big = symbol.toUpperCase();
  const small = symbol.toLowerCase();
  const allele = (dominant: boolean) => (dominant ? big : small);
  const { homo, hetero } = SEX_CHROM[system];
  if (kind === "hemizygous") {
    return `${homo}^${allele(copies >= 1)} ${hetero}`;
  }
  const pair = [copies >= 1, copies >= 2].map(allele);
  if (kind === "homogametic") {
    return `${homo}^${pair[0]} ${homo}^${pair[1]}`;
  }
  return pair.join("");
}

function kindOf(
  geneIndex: number,
  sexLinked: boolean,
  parent: 1 | 2,
  system: SexSystem
): Kind {
  if (geneIndex !== 0 || !sexLinked) {
    return "autosomal";
  }
  const homogameticParent = system === "xy" ? 1 : 2;
  return parent === homogameticParent ? "homogametic" : "hemizygous";
}

function clampCopies(value: string, kind: Kind): number {
  const parsed = Number(value);
  return Math.min(
    Number.isFinite(parsed) ? parsed : 1,
    kind === "hemizygous" ? 1 : 2
  );
}

function kindText(copies: number, kind: Kind, strings: Strings): string {
  if (kind === "hemizygous") {
    return copies >= 1 ? strings.kindHemiDominant : strings.kindHemiRecessive;
  }
  if (copies >= 2) {
    return strings.kindHomDominant;
  }
  return copies === 1 ? strings.kindHet : strings.kindHomRecessive;
}

function ratioText(counts: readonly number[]): string {
  return reduceRatio(counts).join(" : ");
}

function defaultName(
  strings: Strings,
  gene: GeneState,
  phenotypeClass: number
): string {
  const big = gene.symbol.toUpperCase();
  const small = gene.symbol.toLowerCase();
  if (gene.dominance === "complete") {
    return phenotypeClass === 0
      ? t(strings.phenDominant, { allele: big })
      : t(strings.phenRecessive, { allele: small });
  }
  const genotype = [`${big}${big}`, `${big}${small}`, `${small}${small}`][
    phenotypeClass
  ];
  if (phenotypeClass !== 1) {
    return t(strings.phenHomozygous, { genotype });
  }
  const template =
    gene.dominance === "incomplete"
      ? strings.phenIntermediate
      : strings.phenBoth;
  return t(template, { genotype });
}

function phenotypeName(
  strings: Strings,
  gene: GeneState,
  phenotypeClass: number
): string {
  const slot =
    gene.dominance === "complete" && phenotypeClass === 1 ? 2 : phenotypeClass;
  const custom = gene.names[slot].trim();
  return custom === "" ? defaultName(strings, gene, phenotypeClass) : custom;
}

function validSymbols(
  genes: readonly GeneState[]
): "ok" | "letter" | "duplicate" {
  if (!genes.every((gene) => isCasedLetter(gene.symbol))) {
    return "letter";
  }
  const unique = new Set(genes.map((gene) => gene.symbol.toUpperCase()));
  return unique.size === genes.length ? "ok" : "duplicate";
}

export default function PunnettSquareCalculatorWidget() {
  const strings = useToolUi(PUNNETT_SQUARE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [count, setCount] = useState<GeneCount>("2");
  const [sexLinked, setSexLinked] = useState(false);
  const [system, setSystem] = useState<SexSystem>("xy");
  const [genes, setGenes] = useState<GeneState[]>([
    initialGene(0),
    initialGene(1),
    initialGene(2),
  ]);

  const active = genes.slice(0, Number(count));
  const sexual = sexLinked;

  function patchGene(index: number, patch: Partial<GeneState>) {
    setGenes((previous) =>
      previous.map((gene, i) => (i === index ? { ...gene, ...patch } : gene))
    );
  }

  function patchName(index: number, slot: number, value: string) {
    const names = [...genes[index].names] as GeneState["names"];
    names[slot] = value;
    patchGene(index, { names });
  }

  const problem = validSymbols(active);
  const inputs: GeneInput[] = active.map((gene, index) => ({
    symbol: gene.symbol,
    dominance: gene.dominance,
    parent1: clampCopies(gene.parent1, kindOf(index, sexual, 1, system)),
    parent2: clampCopies(gene.parent2, kindOf(index, sexual, 2, system)),
  }));
  const result =
    problem === "ok"
      ? runCross({ genes: inputs, sexLinked: sexual, system })
      : null;
  const parentNames = sexual
    ? [strings.female, strings.male]
    : [strings.parent1, strings.parent2];

  function genotypeSelect(index: number, parent: 1 | 2) {
    const gene = active[index];
    const kind = kindOf(index, sexual, parent, system);
    const top = kind === "hemizygous" ? 1 : 2;
    const choices = Array.from({ length: top + 1 }, (_, i) => top - i);
    const raw = parent === 1 ? gene.parent1 : gene.parent2;
    return (
      <SelectField
        id={`pn-g${index}-p${parent}`}
        label={t(strings.genotypeLabel, {
          parent: parentNames[parent - 1],
          n: fmt(index + 1),
        })}
        onChange={(value) =>
          patchGene(
            index,
            parent === 1 ? { parent1: value } : { parent2: value }
          )
        }
        options={choices.map((copies) => ({
          value: String(copies),
          label: `${genotypeText(gene.symbol, copies, kind, system)} — ${kindText(copies, kind, strings)}`,
        }))}
        value={String(clampCopies(raw, kind))}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.genesLabel}
        name="pn-genes"
        onChange={setCount}
        options={[
          { value: "1", label: strings.genesOne },
          { value: "2", label: strings.genesTwo },
          { value: "3", label: strings.genesThree },
        ]}
        value={count}
      />
      <div className="space-y-3">
        <Checkbox
          checked={sexLinked}
          label={strings.sexLinked}
          onChange={setSexLinked}
        />
        {sexLinked ? (
          <div className="space-y-2">
            <SelectField
              id="pn-system"
              label={strings.systemLabel}
              onChange={setSystem}
              options={[
                { value: "xy", label: strings.systemXy },
                { value: "zw", label: strings.systemZw },
              ]}
              value={system}
            />
            <p className={HINT}>{strings.sexNote}</p>
          </div>
        ) : null}
      </div>
      {active.map((gene, index) => (
        <div className={`${CARD} space-y-4`} key={DEFAULT_SYMBOLS[index]}>
          <h3 className={WIDGET_H3}>
            {t(strings.geneHeading, { n: fmt(index + 1) })}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              hint={strings.symbolHint}
              id={`pn-symbol-${index}`}
              label={t(strings.symbolLabel, { n: fmt(index + 1) })}
              mono
              onChange={(value) =>
                patchGene(index, { symbol: value.slice(-1).toUpperCase() })
              }
              value={gene.symbol}
            />
            <SelectField
              id={`pn-dominance-${index}`}
              label={t(strings.dominanceLabel, { n: fmt(index + 1) })}
              onChange={(value) => patchGene(index, { dominance: value })}
              options={[
                { value: "complete", label: strings.dominanceComplete },
                { value: "incomplete", label: strings.dominanceIncomplete },
                { value: "codominant", label: strings.dominanceCodominant },
              ]}
              value={gene.dominance}
            />
            {genotypeSelect(index, 1)}
            {genotypeSelect(index, 2)}
          </div>
        </div>
      ))}
      <details className={`${CARD} space-y-3`}>
        <summary className="cursor-pointer font-medium text-foreground text-sm">
          {strings.namesSummary}
        </summary>
        <div className="grid gap-4 pt-3 sm:grid-cols-2">
          {active.flatMap((gene, index) =>
            (gene.dominance === "complete" ? [0, 2] : [0, 1, 2]).map((slot) => {
              const classIndex =
                gene.dominance === "complete" && slot === 2 ? 1 : slot;
              const big = gene.symbol.toUpperCase();
              const small = gene.symbol.toLowerCase();
              const genotype = [
                `${big}${big}`,
                `${big}${small}`,
                `${small}${small}`,
              ][slot];
              return (
                <TextField
                  id={`pn-name-${index}-${slot}`}
                  key={`${DEFAULT_SYMBOLS[index]}-${slot}`}
                  label={t(strings.nameLabel, { n: fmt(index + 1), genotype })}
                  onChange={(value) => patchName(index, slot, value)}
                  placeholder={defaultName(strings, gene, classIndex)}
                  value={gene.names[slot]}
                />
              );
            })
          )}
        </div>
      </details>
      {problem === "ok" ? null : (
        <ErrorNotice>
          {problem === "letter" ? strings.errorSymbol : strings.errorDuplicate}
        </ErrorNotice>
      )}
      {result ? (
        <Results
          active={active}
          fmt={fmt}
          locale={locale}
          parentNames={parentNames}
          result={result}
          strings={strings}
        />
      ) : null}
    </div>
  );
}

function Results({
  result,
  active,
  parentNames,
  strings,
  fmt,
  locale,
}: {
  result: CrossResult;
  active: readonly GeneState[];
  parentNames: readonly string[];
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  locale: string;
}) {
  const phenotypeLabels = result.phenotypes.map((phenotype) => {
    const traits = phenotype.phenotypeClasses
      .map((cls, index) => phenotypeName(strings, active[index], cls))
      .join(", ");
    if (phenotype.sex === null) {
      return traits;
    }
    const sex =
      phenotype.sex === "female"
        ? strings.phenotypeFemale
        : strings.phenotypeMale;
    return t(strings.phenotypeWithSex, { sex, traits });
  });
  const phenotypeOfGenotype = new Map<number, number>();
  for (const row of result.cells) {
    for (const cell of row) {
      phenotypeOfGenotype.set(cell.genotype, cell.phenotype);
    }
  }
  const genotypeCounts = result.genotypes.map((g) => g.count);
  const phenotypeCounts = result.phenotypes.map((p) => p.count);
  const share = fmt(100 / result.total, { maximumFractionDigits: 2 });
  const copyText = [
    `${strings.genotypicRatio}: ${ratioText(genotypeCounts)}`,
    `${strings.phenotypicRatio}: ${ratioText(phenotypeCounts)}`,
    ...result.phenotypes.map(
      (p, i) => `${phenotypeLabels[i]}: ${p.count}/${result.total}`
    ),
  ].join("\n");

  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.gametesHeading}</h3>
        {[result.rows, result.cols].map((gametes, index) => (
          <p className="text-foreground text-sm" key={parentNames[index]}>
            {t(strings.gametesLine, { parent: parentNames[index] })}{" "}
            {gametes.map((gamete, i) => (
              <span key={gamete.key}>
                {i > 0 ? ", " : null}
                <GameteLabel gamete={gamete} />
              </span>
            ))}
          </p>
        ))}
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.squareHeading}</h3>
        <SquareTable
          caption={t(strings.squareCaption, {
            rowParent: parentNames[0],
            colParent: parentNames[1],
          })}
          cellLabel={(genotype, phenotype) =>
            t(strings.boxLabel, {
              genotype: geneListText(result.genotypes[genotype].genes),
              phenotype: phenotypeLabels[phenotype],
            })
          }
          cornerLabel={strings.squareCorner}
          result={result}
        />
        <p className={HINT}>
          {fmt(result.rows.length)} × {fmt(result.cols.length)} ={" "}
          {tPlural(strings.boxesSummary, locale, result.total, {
            count: fmt(result.total),
            percent: share,
          })}
        </p>
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.phenotypesHeading}</h3>
        <CategoryTable
          fmt={fmt}
          head={[
            strings.phenotypeColumn,
            "",
            strings.boxesColumn,
            strings.fractionColumn,
            strings.probabilityColumn,
          ]}
          rows={result.phenotypes.map((p, i) => ({
            key: p.key,
            label: phenotypeLabels[i],
            count: p.count,
            tint: tintFor(i),
          }))}
          total={result.total}
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.genotypesHeading}</h3>
        <CategoryTable
          fmt={fmt}
          head={[
            strings.genotypeColumn,
            strings.phenotypeColumn,
            strings.boxesColumn,
            strings.fractionColumn,
            strings.probabilityColumn,
          ]}
          rows={result.genotypes.map((g, i) => ({
            key: g.key,
            label: <GeneLabels genes={g.genes} />,
            secondary: phenotypeLabels[phenotypeOfGenotype.get(i) ?? 0],
            count: g.count,
          }))}
          total={result.total}
        />
      </div>
      <RatioBlock
        copyText={copyText}
        genotypeCounts={genotypeCounts}
        phenotypeCounts={phenotypeCounts}
        strings={strings}
      />
    </div>
  );
}

function RatioBlock({
  genotypeCounts,
  phenotypeCounts,
  copyText,
  strings,
}: {
  genotypeCounts: readonly number[];
  phenotypeCounts: readonly number[];
  copyText: string;
  strings: Strings;
}) {
  const lines = [
    { label: strings.genotypicRatio, counts: genotypeCounts },
    { label: strings.phenotypicRatio, counts: phenotypeCounts },
  ];
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.ratioHeading}</h3>
      {lines.map((line) => {
        const inOrder = ratioText(line.counts);
        const sorted = ratioText([...line.counts].sort((a, b) => b - a));
        return (
          <div key={line.label}>
            <p className="text-foreground text-sm">
              <span className="font-medium">{line.label}:</span>{" "}
              <span className="font-mono">{inOrder}</span>
            </p>
            {sorted === inOrder ? null : (
              <p className={HINT}>
                {t(strings.ratioLargest, { label: line.label })}:{" "}
                <span className="font-mono">{sorted}</span>
              </p>
            )}
          </div>
        );
      })}
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyRatios}
        text={copyText}
      />
    </div>
  );
}
